// Import Dependencies
import { Fragment, useState } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FiCheck } from "react-icons/fi";

// Local Imports
import { Button, Input, Select, Textarea } from "components/ui";

// ----------------------------------------------------------------------

export function MasterFieldPreviewModal({ isOpen, onClose, masterField, onEdit }) {
  const [previewValue, setPreviewValue] = useState(null);

  if (!masterField) return null;

  // Normalize field data
  const normalizedField = {
    id: masterField.Id || masterField.id || "preview-field",
    label: masterField.FieldLabel || masterField.fieldLabel || "Field",
    type: masterField.HtmlInputType || masterField.htmlInputType || masterField.fieldType || masterField.type || "text",
    placeholder: masterField.Placeholder || masterField.placeholder || "",
    required: masterField.IsRequired !== undefined ? masterField.IsRequired : (masterField.isRequired || false),
    options: masterField.MasterFormFieldOptions || masterField.options || []
  };

  // Render field input based on type
  const renderFieldInput = () => {
    if (normalizedField.type === "text" || normalizedField.type === "email" || 
        normalizedField.type === "tel" || normalizedField.type === "number" || 
        normalizedField.type === "date" || normalizedField.type === "url" || 
        normalizedField.type === "time" || normalizedField.type === "password") {
      return (
        <Input
          type={normalizedField.type}
          placeholder={normalizedField.placeholder || `Enter ${normalizedField.label.toLowerCase()}`}
          value={previewValue || ''}
          onChange={(e) => setPreviewValue(e.target.value)}
          className="w-full"
        />
      );
    } else if (normalizedField.type === "select") {
      return (
        <Select
          value={previewValue || ''}
          onChange={(e) => setPreviewValue(e.target.value)}
          className="w-full"
        >
          <option value="">{normalizedField.placeholder || `Select ${normalizedField.label}`}</option>
          {(!normalizedField.options || normalizedField.options.length === 0) ? (
            <option value="" disabled>No options available</option>
          ) : (
            normalizedField.options.map((option, index) => {
              let optionValue, optionLabel;
              if (typeof option === 'string') {
                optionValue = option || `option_${index}`;
                optionLabel = option || `Option ${index + 1}`;
              } else {
                optionValue = option?.OptionValue || option?.optionValue || option?.value || `option_${index}`;
                optionLabel = option?.OptionLabel || option?.optionLabel || option?.label || `Option ${index + 1}`;
              }
              
              return (
                <option key={index} value={optionValue}>
                  {optionLabel}
                </option>
              );
            })
          )}
        </Select>
      );
    } else if (normalizedField.type === "textarea") {
      return (
        <Textarea
          placeholder={normalizedField.placeholder || `Enter ${normalizedField.label.toLowerCase()}`}
          value={previewValue || ''}
          onChange={(e) => setPreviewValue(e.target.value)}
          rows={4}
          className="w-full"
        />
      );
    } else if (normalizedField.type === "file") {
      return (
        <input
          type="file"
          onChange={(e) => setPreviewValue(e.target.files?.[0]?.name || '')}
          className="w-full px-3 py-2 border border-gray-300 dark:border-dark-500 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      );
    } else if (normalizedField.type === "checkbox") {
      if (!normalizedField.options || normalizedField.options.length === 0) {
        return (
          <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              No options available for this checkbox field.
            </p>
          </div>
        );
      }
      
      return (
        <div className="space-y-2 p-1">
          {normalizedField.options.map((option, index) => {
            let optionValue, optionLabel;
            if (typeof option === 'string') {
              optionValue = option || `option_${index}`;
              optionLabel = option || `Option ${index + 1}`;
            } else {
              optionValue = option?.OptionValue || option?.optionValue || option?.value || `option_${index}`;
              optionLabel = option?.OptionLabel || option?.optionLabel || option?.label || `Option ${index + 1}`;
            }
            
            const currentValues = previewValue || [];
            const isChecked = currentValues.includes(optionValue);
            
            return (
              <label key={index} className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-600 p-2 rounded-md transition-colors duration-150">
                <div className="relative mr-3">
                  <input 
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      const newValues = e.target.checked 
                        ? [...currentValues, optionValue]
                        : currentValues.filter(v => v !== optionValue);
                      setPreviewValue(newValues);
                    }}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 transition-all duration-200 cursor-pointer flex items-center justify-center ${
                    isChecked 
                      ? 'bg-blue-600 border-blue-600' 
                      : 'bg-white dark:bg-dark-700 border-gray-300 dark:border-dark-500 hover:border-blue-400'
                  }`}>
                    {isChecked && (
                      <FiCheck className="w-3 h-3 text-white" />
                    )}
                  </div>
                </div>
                <span className="text-sm text-gray-700 dark:text-dark-300 font-medium select-none">
                  {optionLabel}
                </span>
              </label>
            );
          })}
        </div>
      );
    } else if (normalizedField.type === "radio") {
      if (!normalizedField.options || normalizedField.options.length === 0) {
        return (
          <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              No options available for this radio field.
            </p>
          </div>
        );
      }
      
      return (
        <div className="space-y-2 p-1">
          {normalizedField.options.map((option, index) => {
            let optionValue, optionLabel;
            if (typeof option === 'string') {
              optionValue = option || `option_${index}`;
              optionLabel = option || `Option ${index + 1}`;
            } else {
              optionValue = option?.OptionValue || option?.optionValue || option?.value || `option_${index}`;
              optionLabel = option?.OptionLabel || option?.optionLabel || option?.label || `Option ${index + 1}`;
            }
            
            return (
              <label key={index} className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-600 p-2 rounded-md transition-colors duration-150">
                <div className="relative mr-3">
                  <input
                    type="radio"
                    name="preview-radio"
                    value={optionValue}
                    checked={previewValue === optionValue}
                    onChange={(e) => setPreviewValue(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full border-2 transition-all duration-200 cursor-pointer flex items-center justify-center ${
                    previewValue === optionValue 
                      ? 'bg-blue-600 border-blue-600' 
                      : 'bg-white dark:bg-dark-700 border-gray-300 dark:border-dark-500 hover:border-blue-400'
                  }`}>
                    {previewValue === optionValue && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                </div>
                <span className="text-sm text-gray-700 dark:text-dark-300 font-medium select-none">
                  {optionLabel}
                </span>
              </label>
            );
          })}
        </div>
      );
    }
    
    // Default fallback
    return (
      <Input
        type="text"
        placeholder={normalizedField.placeholder || 'Enter text...'}
        value={previewValue || ''}
        onChange={(e) => setPreviewValue(e.target.value)}
        className="w-full"
      />
    );
  };

  // Reset preview value when modal closes
  const handleClose = () => {
    setPreviewValue(normalizedField.type === "checkbox" ? [] : null);
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[100000] flex flex-col items-center justify-center overflow-hidden px-2 py-4 sm:px-4 sm:py-6"
        onClose={()=>{}}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative flex w-full max-w-2xl origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-dark-700 mx-4 sm:mx-0">
            {/* Header */}
            <div className="rounded-t-lg bg-gray-200 px-4 sm:px-6 py-4 dark:bg-dark-800">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <DialogTitle
                    as="h3"
                    className="text-base sm:text-lg font-semibold text-gray-800 dark:text-dark-100"
                  >
                    Field Preview
                  </DialogTitle>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-dark-300 mt-1">
                    This is how the field will appear in your form
                  </p>
                </div>
                <Button
                  onClick={handleClose}
                  variant="flat"
                  isIcon
                  className="size-8 rounded-full cursor-pointer flex-shrink-0"
                >
                  <XMarkIcon className="size-5" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6">
              <div className="mb-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-dark-100 mb-1">
                      {normalizedField.label}
                      {normalizedField.required && <span className="text-red-500 ml-1">*</span>}
                    </h4>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 px-2 py-1 rounded-full font-medium">
                        Master Field
                      </span>
                      <span className="text-xs bg-gray-100 text-gray-700 dark:bg-dark-600 dark:text-dark-300 px-2 py-1 rounded-full font-medium">
                        {normalizedField.type}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Field Preview */}
                <div className="border border-gray-200 dark:border-dark-600 rounded-lg p-6 bg-gray-50 dark:bg-dark-800">
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
                    {normalizedField.label}
                    {normalizedField.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {renderFieldInput()}
                  
                  {normalizedField.placeholder && normalizedField.type !== "select" && normalizedField.type !== "checkbox" && normalizedField.type !== "radio" && (
                    <p className="text-xs text-gray-500 dark:text-dark-400 mt-2">
                      Placeholder: &quot;{normalizedField.placeholder}&quot;
                    </p>
                  )}
                </div>

                {/* Field Information */}
                <div className="mt-4 space-y-2">
                  <div className="flex items-center text-sm">
                    <span className="font-medium text-gray-700 dark:text-dark-200 w-32">Field Name:</span>
                    <span className="text-gray-600 dark:text-dark-300">{masterField.FieldName || masterField.fieldName || "N/A"}</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <span className="font-medium text-gray-700 dark:text-dark-200 w-32">Field Type:</span>
                    <span className="text-gray-600 dark:text-dark-300 capitalize">{normalizedField.type}</span>
                  </div>
                  {(normalizedField.type === "select" || normalizedField.type === "checkbox" || normalizedField.type === "radio") && (
                    <div className="flex items-start text-sm">
                      <span className="font-medium text-gray-700 dark:text-dark-200 w-32">Options:</span>
                      <span className="text-gray-600 dark:text-dark-300">
                        {normalizedField.options && normalizedField.options.length > 0 
                          ? `${normalizedField.options.length} option(s)` 
                          : "No options"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 dark:border-dark-600 px-4 sm:px-6 py-4">
              <div className="flex justify-end gap-3">
                {masterField?.IsCompanyMasterField === 1 && onEdit && (
                  <Button
                    onClick={() => {
                      onEdit();
                      handleClose();
                    }}
                    variant="outlined"
                    className="min-w-[100px] cursor-pointer text-blue-600 border-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-400 dark:hover:bg-blue-900/20"
                  >
                    Edit
                  </Button>
                )}
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  className="min-w-[100px] cursor-pointer"
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}
